import { parse } from "@loaders.gl/core"
import { retryWithBackoff } from "@nova/util-retry"

export type CogTileRequest = {
    x: number
    y: number
    z: number
    tileSize: 256 | 512
}

export type CogTileSource = (
    request: CogTileRequest,
    signal: AbortSignal | undefined
) => Promise<Response>

const empty = { status: "empty" } as const

export const fetchCogTile = async (
    source: CogTileSource,
    request: CogTileRequest,
    signal: AbortSignal | undefined
) => {
    const result = await retryWithBackoff(
        async () => {
            if (signal?.aborted) return empty
            try {
                const response = await source(request, signal)
                if (signal?.aborted) return empty
                if (!response.ok) {
                    if (response.status < 500) return empty
                    return {
                        status: "failed",
                        error: new Error(`Cog tile server error: ${response.status}`),
                    } as const
                }
                const blob = await response.blob()
                if (signal?.aborted) return empty
                const image = (await parse(blob.slice(0, blob.size, "image/png"))) as ImageData
                return { status: "loaded", image } as const
            } catch (error) {
                return signal?.aborted ? empty : ({ status: "failed", error } as const)
            }
        },
        {
            maxAttempts: 3,
            baseMs: 250,
            shouldRetry: (result) => result.status === "failed",
        }
    )

    if (result.status === "failed") throw result.error
    return result.status === "loaded" ? result.image : null
}
